import React, { useState } from 'react';
import { Search, X } from 'lucide-react';

interface FilterOption {
    id: number;
    nome: string;
}

interface TagSelectorProps {
    tags: FilterOption[];
    selected: number[];
    onChange: (ids: number[]) => void;
    loading?: boolean;
}

export const TagSelector: React.FC<TagSelectorProps> = ({ tags, selected, onChange, loading = false }) => {
    const [search, setSearch] = useState('');

    const toggle = (id: number) => {
        if (selected.includes(id)) {
            onChange(selected.filter((s) => s !== id));
        } else {
            onChange([...selected, id]);
        }
    };

    const filtered = search.trim()
        ? tags.filter((t) => t.nome.toLowerCase().includes(search.toLowerCase()))
        : tags;

    if (loading) {
        return (
            <div className="flex flex-wrap gap-2">
                {Array.from({ length: 12 }).map((_, i) => (
                    <div
                        key={i}
                        className="h-7 animate-pulse rounded-full bg-white/[0.06]"
                        style={{ width: `${48 + (i % 5) * 16}px` }}
                    />
                ))}
            </div>
        );
    }

    if (tags.length === 0) {
        return (
            <p className="text-[11px] text-white/30 italic">Nenhuma tag disponível para este formato.</p>
        );
    }

    return (
        <div className="space-y-3">
            {tags.length > 8 && (
                <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filtrar tags…"
                        className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-1.5 pl-8 pr-8 text-xs text-white placeholder:text-white/30 outline-none focus:border-orange-500/40 focus:bg-white/[0.05]"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
            )}

            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto overscroll-contain pr-0.5">
                {filtered.length === 0 ? (
                    <p className="text-[11px] text-white/30 italic">Nenhuma tag encontrada.</p>
                ) : (
                    filtered.map((tag) => {
                        const isSelected = selected.includes(tag.id);
                        return (
                            <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggle(tag.id)}
                                className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${
                                    isSelected
                                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40 hover:bg-orange-500/30'
                                        : 'bg-white/[0.05] text-white/55 border border-white/10 hover:bg-white/10 hover:text-white/80'
                                }`}
                            >
                                {isSelected && <span className="mr-1 text-orange-400">✓</span>}
                                {tag.nome}
                            </button>
                        );
                    })
                )}
            </div>

            {selected.length > 0 && (
                <button
                    type="button"
                    onClick={() => onChange([])}
                    className="text-[10px] font-semibold uppercase tracking-wide text-white/35 hover:text-white/60 transition-colors"
                >
                    Limpar tags ({selected.length})
                </button>
            )}
        </div>
    );
};
